import type { JSX, ReactNode } from 'react';
import { Icon } from './Icon';
import { Button } from './Button';
import { AutoMeter } from './Metrics';

/**
 * One step's state in GUIDE mode. `complete` earns a check; a `requirement` on a step
 * the user has already opened earns an amber dot and names what is outstanding. A step
 * with neither is simply open — no dot, no check, no false claim that walking past it
 * finished it.
 */
export interface StepGuideState {
  readonly complete: boolean;
  readonly requirement?: string | undefined;
}

/**
 * Horizontal wizard stepper.
 *
 * Two modes, one component.
 *
 * GATED (the default, every existing agent): completed steps are clickable so an
 * assessor can go back and check an input; steps ahead of the current one are
 * disabled, because the wizard gates on the data collected so far. The prototype
 * rendered these as decorative divs, which left the "go back" affordance
 * keyboard-inaccessible.
 *
 * GUIDE (`states` supplied): every step is reachable at any time. The strip guides, it
 * does not gate — Continue is what gates. Jumping ahead teaches a demo audience nothing
 * about what is missing; naming the missing input does (MMM NEW-20). Each step reports
 * its own state, so the strip and the Continue button read one source of truth.
 */
export function Steps({
  titles,
  current,
  onSelect,
  states,
}: {
  readonly titles: readonly string[];
  /** 1-based. */
  readonly current: number;
  readonly onSelect: (step: number) => void;
  /** One entry per title. Supplying this switches the strip to guide mode. */
  readonly states?: readonly StepGuideState[] | undefined;
}): JSX.Element {
  const guide = states !== undefined;
  return (
    <ol className="steps" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {titles.map((title, i) => {
        const n = i + 1;
        const st = states?.[i];
        const done = guide ? st?.complete === true : current > n;
        const need = guide && st?.complete !== true ? st?.requirement : undefined;
        const todo = need !== undefined;
        const state = current === n ? 'now' : done ? 'done' : todo ? 'todo' : '';
        const lineDone = guide ? done : current > n;
        return (
          <li key={title} style={{ display: 'flex', alignItems: 'center' }}>
            {i > 0 && <span className={`step-line ${lineDone ? 'done' : ''}`} />}
            <div className={`step ${state}`}>
              <button
                type="button"
                className="step-btn"
                disabled={!guide && current < n}
                aria-current={current === n ? 'step' : undefined}
                title={need === undefined ? undefined : `Still needed: ${need}`}
                onClick={() => {
                  onSelect(n);
                }}
              >
                <span className="step-dot">
                  {done ? <Icon name="check" /> : todo ? <span className="step-todo" /> : n}
                </span>
                <span className="st-lb">{title}</span>
              </button>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** Footer navigation for a wizard step. */
export function WizardNav({
  back,
  next,
  extra,
}: {
  readonly back?: ReactNode | undefined;
  readonly next?: ReactNode | undefined;
  /** Additional controls placed next to the back button. */
  readonly extra?: ReactNode | undefined;
}): JSX.Element {
  return (
    <div className="wiz-nav">
      <div className="btn-group">
        {back}
        {extra}
      </div>
      {next}
    </div>
  );
}

export function BackButton({
  onClick,
  label = 'Back',
  disabled = false,
}: {
  readonly onClick: () => void;
  readonly label?: string | undefined;
  readonly disabled?: boolean | undefined;
}): JSX.Element {
  return (
    <Button variant="secondary" icon="arrowL" onClick={onClick} disabled={disabled}>
      {label}
    </Button>
  );
}

export function NextButton({
  onClick,
  label,
  disabled = false,
}: {
  readonly onClick: () => void;
  readonly label: string;
  readonly disabled?: boolean | undefined;
}): JSX.Element {
  return (
    <Button variant="primary" iconAfter="arrowR" onClick={onClick} disabled={disabled}>
      {label}
    </Button>
  );
}

export interface SideStepDef {
  readonly id: string;
  readonly title: string;
  /** Completion percentage; 100 marks the step done. */
  readonly percent: number;
  readonly caption?: string | undefined;
}

/** Vertical outline used by the activation report builder. */
export function SideSteps({
  steps,
  active,
  onSelect,
}: {
  readonly steps: readonly SideStepDef[];
  readonly active: string;
  readonly onSelect: (id: string) => void;
}): JSX.Element {
  return (
    <div className="side-steps">
      {steps.map((s, i) => {
        const state = active === s.id ? 'now' : s.percent === 100 ? 'done' : '';
        return (
          <button
            key={s.id}
            type="button"
            className={`side-step ${state}`}
            aria-current={active === s.id ? 'true' : undefined}
            onClick={() => {
              onSelect(s.id);
            }}
          >
            <span className="n">{s.percent === 100 ? <Icon name="check" /> : i + 1}</span>
            <span style={{ flex: 1 }}>
              <span className="t">{s.title}</span>
              <div style={{ marginTop: 4 }}>
                <AutoMeter value={s.percent} ariaLabel={`${s.title} completeness`} />
              </div>
              {s.caption !== undefined && <span className="caption">{s.caption}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}
